<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-data-table-server
    v-model:items-per-page="itemsPerPage"
    v-model:sort-by="sortBy"
    v-model="selection"
    class="narrow-borders"
    :items="serverItems"
    :items-length="totalItems || 0"
    :headers="selectedHeadersComputed"
    :search="search"
    :loading="loading"
    :mobile="showExpanded"
    :show-select="!!bulkActions"
    :multi-sort="true"
    :page="currentPage"
    :item-value="itemValue"
    :return-object="returnObject"
    @update:page="updatePage"
    @update:options="loadItems"
  >
    <template #top>
      <v-toolbar
        flat
        density="compact"
      >
        <v-toolbar-items>
          <v-dialog
            v-if="createEnabled"
            v-model="dialog"
            persistent
            :width="!xs ? 'auto' : '100000px'"
            :fullscreen="xs"
          >
            <template #activator="{ props }">
              <v-btn
                color="primary"
                v-bind="props"
                icon="fas fa-plus"
                size="small"
              />
            </template>
            <component
              :is="editionComponent"
              mode="create"
              @model:created="loadItems"
              @close="dialog = false;"
            />
          </v-dialog>
        </v-toolbar-items>
        <v-toolbar-items class="d-flex align-center flex-grow-1">
          <v-text-field
            v-if="searchEnabled"
            v-model="tempSearchText"
            class="mx-2"
            flat
            clearable
            density="compact"
            :label="t('global.search')"
            variant="solo"
            hide-details
            @click:clear="searchModel"
            @update:model-value="searchModel"
          >
            <template #prepend-inner>
              <v-icon
                icon="fas fa-search"
                size="x-small"
              />
            </template>
          </v-text-field>
        </v-toolbar-items>
        <slot name="globalActions" />
        <v-icon
          color="primary"
          :icon="showExpanded ? 'fas fa-caret-up' : 'fas fa-caret-down'"
          size="small"
          @click="showExpanded = !showExpanded"
        />
        <v-toolbar-items>
          <vuetify-data-table-column-select
            v-if="columnSelectionEnabled"
            v-model="selectedHeaders"
            :headers="headers"
          />
          <component
            :is="action.component"
            v-for="(action, $index) in bulkActions"
            v-bind="action.props"
            :key="$index"
            :disabled="!selection.length"
            :selection="selection"
            @reload="loadItems"
            @click="runCallback(action, selection)"
          />
        </v-toolbar-items>
      </v-toolbar>
    </template>

    <template #item.subpoints="{ item }">
      <ul>
        <li
          v-for="(itm, index) in item.subpoints"
          :key="index"
        >
          {{ itm }}
        </li>
      </ul>
    </template>

    <template #item.tags="{ item }">
      <div
        v-if="
          !item.tags.length"
      >
        <span class="text-grey">{{ t('tags.no_tags') }}</span>
      </div>
      <div v-else>
        <v-chip
          v-for="tag in item.tags"
          :key="tag"
          class="me-1"
        >
          {{ tag }}
        </v-chip>
      </div>
    </template>

    <template #item.profile_image_url="{ item }">
      <user-avatar
        :user="item"
        size="35px"
        class="ma-1"
      />
    </template>

    <template #item.icon="{ item }">
      <v-icon :icon="`fas fa-${item.icon}`" />
    </template>

    <template
      v-for="header in customColumns"
      #[`item.${header}`]="{ item }"
    >
      <slot
        :name="`item.${header}`"
        v-bind="{ item }"
      />
    </template>

    <template #item.actions="{ item }">
      <v-toolbar
        density="compact"
        flat
        color="transparent"
      >
        <v-toolbar-items class="w-100">
          <component
            :is="action.component"
            v-for="(action, $index) in additionalActions"
            v-bind="action.props"
            :key="$index"
            :model="item"
            @reload="loadItems"
            @click="runCallback(action, item)"
          />
          <slot
            name="actions"
            v-bind="{ item, loadItems }"
          />
          <v-dialog
            v-if="editEnabled"
            persistent
            :width="!xs ? 'auto' : undefined"
            :fullscreen="xs"
            :retain-focus="false"
          >
            <template #activator="{ props: btnProps }">
              <v-btn
                v-tooltip="'Edit'"
                color="primary"
                v-bind="btnProps"
                size="small"
                icon="fa fa-pen-to-square"
                :text="t('actions.edit')"
                :title="t('actions.edit')"
              />
            </template>

            <template #default="{ isActive }">
              <component
                :is="editionComponent"
                :model-id="item.id"
                mode="edit"
                @model:updated="loadItems"
                @close="isActive.value = false"
              />
            </template>
          </v-dialog>
          <deletion-dialog
            v-if="deleteEnabled"
            :model-name="modelName"
            :delete-url="getDeletionUrl(url, item?.id)"
            size="small"
            @model:deleted="loadItems"
          />
        </v-toolbar-items>
      </v-toolbar>
    </template>
  </v-data-table-server>
</template>

<script setup lang="ts">
import type { AxiosError } from 'axios';
import axios from 'axios';
import debounce from 'lodash/debounce';
import {
  computed,
  ref,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useDisplay } from 'vuetify';
import UserAvatar from '@/components/images/UserAvatar.vue';
import { useDataTableUtilities } from '@/composables/index.ts';
import { useErrorStore } from '@/stores/errors';
import DeletionDialog from './DeletionDialog.vue';
import VuetifyDataTableColumnSelect from './VuetifyDataTableColumnSelect.vue';

const properties = withDefaults(defineProps<{
  url: string;
  headers: Array<any>;
  modelName: string;
  createEnabled?: boolean;
  returnObject?: boolean;
  editEnabled?: boolean;
  deleteEnabled?: boolean;
  searchEnabled?: boolean;
  columnSelectionEnabled?: boolean;
  editionComponent?: object;
  filters?: Array<SearchFilter>;
  includes?: Array<string>;
  additionalActions?: Array<any>;
  bulkActions?: Array<any>;
  customColumns?: Array<string>;
  itemValue?: string;
}>(), {
  createEnabled: () => true,
  itemValue: 'name',
  editEnabled: () => true,
  returnObject: () => false,
  deleteEnabled: () => true,
  columnSelectionEnabled: () => true,
  searchEnabled: () => true,
  filters: () => [],
  includes: () => [],
  customColumns: () => [],
});
const { xs } = useDisplay();
const { t } = useI18n();

const showExpanded = ref(false);
const selection = ref<Array<any>>([]);
const { showSnackMessage } = useErrorStore();
const { getDataTableUrl, getDeletionUrl, watchString, watchInt, getStringFromQuery, watchSortBy, currentPage, search, itemsPerPage } = useDataTableUtilities();

type ServerItem = App.Models.User & {
  [key: string]: any;
  subpoints?: string;
  icon?: string;
  order?: number;
};

const selectedHeaders = ref<any>([]);
const totalItems = ref(0);
const dialog = ref(false);
const loading = ref(true);
const serverItems = ref<ServerItem[]>([]);

const sortBy = ref<SortItem[]>([]);
const route = useRoute();

const tempSearchText = ref<string>(getStringFromQuery(route.query?.search, ''));

const savedSearch = sessionStorage.getItem(`${properties.url}search`);
if (savedSearch && !search.value && savedSearch !== 'null') {
  search.value = savedSearch;
  tempSearchText.value = search.value;
}

const searchModel = debounce(() => {
  search.value = tempSearchText.value;
  sessionStorage.setItem(`${properties.url}search`, search.value ?? '');
}, 500);

function runCallback(action, item) {
  if (action.callback) {
    action.callback(item);
  }
}

function updatePage(page) {
  currentPage.value = page;
}

watchSortBy(sortBy);
watchString(search, 'search', '');
watchInt(currentPage, 'page', 1);
watchInt(itemsPerPage, 'per_page', 25);

async function loadItems() {
  loading.value = true;

  const url = getDataTableUrl(
    properties.url,
    properties.includes,
    itemsPerPage.value,
    currentPage.value,
    search.value ?? '',
    [],
    sortBy.value,
    null,
    properties.filters,
  );
  try {
    const response = await axios.get(url);
    totalItems.value = response.data.total ?? response.data.meta.total;
    serverItems.value = response.data.data;
  }
  catch (error) {
    const errors = error as Error | AxiosError;
    let errorMessage = t('errors.cannot_load');
    if (axios.isAxiosError(errors)) {
      errorMessage = errors?.response?.data?.message;
    }
    showSnackMessage(errorMessage);
  }
  finally {
    loading.value = false;
  }
}

selectedHeaders.value = properties.headers.map((header) => header.key);
const selectedHeadersComputed = computed(() => properties.headers.filter((header) => selectedHeaders.value.includes(header.key)));

defineExpose({ loadItems });
</script>

<!--
You can use a template, as per the below example, to define the global actions for the table.
   <template #globalActions>
      <v-toolbar-items class="d-flex align-center">
        <v-select
          v-model="state"
          density="compact"
          hide-details
          :items="states"
          variant="plain"
          class="mb-2"
          @update:model-value="reload"
        />
        <v-menu :close-on-content-click="false">
          <template #activator="{ props: btnProps }">
            <v-btn
              v-bind="btnProps"
              icon="fas fa-filter"
              color="grey-darken-1"
            />
          </template>
          <v-card min-width="300">
            <v-card-text>
              <v-switch
                v-model="assigned"
                :label="t('experience.assigned_to_me')"
                color="primary"
                hide-details
                @update:model-value="reload"
              />
            </v-card-text>
          </v-card>
        </v-menu>
      </v-toolbar-items>
    </template> -->
